import React, { useEffect, useMemo, useRef, useState } from "react";
import { CircularProgress } from "@material-ui/core";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { useTranslation } from "react-i18next";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { GOOGLE_KEY } from "services/baseURLs.config";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { LocationSelection, parseAddressSelection } from "./locationMap.logic";
import {
  DesignV2AutocompleteContainer,
  DesignV2ClearButton,
  DesignV2FieldWrapper,
  DesignV2Input,
  DesignV2LoadingRow,
  DesignV2SuggestionItem,
  DesignV2SuggestionList,
} from "./locationMap.styled";

const DEFAULT_CENTER = {
  lat: 59.334591,
  lng: 18.06324,
};

const PLACES_LIBRARIES: ["places"] = ["places"];

const MAP_CONTAINER_STYLE: React.CSSProperties = {
  width: "100%",
  height: "300px",
};

type Props = {
  address?: string;
  inRegistration?: boolean;
  noMap?: boolean;
  onLocationChange: (selection: LocationSelection | null) => void;
};

export const LocationMap: React.FC<Props> = ({
  address,
  inRegistration,
  noMap,
  onLocationChange,
}) => {
  const { t } = useTranslation();
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_KEY,
    libraries: PLACES_LIBRARIES,
  });
  const [companyState] = useCompanyStore();
  const [addressString, setAddressString] = useState("");
  const [lat, setLat] = useState<number | null>(null);
  const [lng, setLng] = useState<number | null>(null);
  const [validAddress, setValidAddress] = useState(true);
  const [isFocused, setIsFocused] = useState(false);
  const selectionRequestIdRef = useRef(0);
  const hasUserEditedAddressRef = useRef(false);
  const companyAddress = companyState.company?.address;
  const companyCity = companyState.company?.city;
  const companyLocation = companyState.company?.location;
  const companyName = companyState.company?.companyName ?? "";
  const mapCenter = useMemo(
    () => ({
      lat: lat ?? DEFAULT_CENTER.lat,
      lng: lng ?? DEFAULT_CENTER.lng,
    }),
    [lat, lng],
  );
  const markerPosition = useMemo(
    () => (lat === null || lng === null ? null : { lat, lng }),
    [lat, lng],
  );

  useEffect(() => {
    if (!inRegistration) {
      hasUserEditedAddressRef.current = false;
      return;
    }

    if (hasUserEditedAddressRef.current) {
      return;
    }

    setAddressString(address ?? "");
  }, [address, inRegistration]);

  useEffect(() => {
    if (inRegistration) {
      return;
    }

    if (companyLocation) {
      const [nextLat, nextLng] = companyLocation
        .split(",")
        .map((coordinate) => Number(coordinate.trim()));

      setLat(Number.isFinite(nextLat) ? nextLat : null);
      setLng(Number.isFinite(nextLng) ? nextLng : null);
    } else {
      setLat(null);
      setLng(null);
    }

    if (companyAddress) {
      setAddressString(
        companyCity ? `${companyAddress}, ${companyCity}` : companyAddress,
      );
    } else {
      setAddressString("");
    }
  }, [companyAddress, companyCity, companyLocation, inRegistration]);

  const clearResolvedLocation = (): void => {
    onLocationChange(null);
    setLat(null);
    setLng(null);
  };

  const clearLocationData = (): void => {
    hasUserEditedAddressRef.current = true;
    selectionRequestIdRef.current += 1;
    setAddressString("");
    clearResolvedLocation();
    setValidAddress(true);
  };

  const markAddressInvalid = (): void => {
    clearResolvedLocation();
    setValidAddress(false);
  };

  const handleAddressChange = (nextAddress: string): void => {
    hasUserEditedAddressRef.current = true;
    selectionRequestIdRef.current += 1;
    setAddressString(nextAddress);
    clearResolvedLocation();
    setValidAddress(true);
  };

  const applyLocationSelection = (selection: LocationSelection): void => {
    setLat(selection.lat);
    setLng(selection.lng);
    setValidAddress(true);
    setAddressString(selection.formattedAddress);
    onLocationChange(selection);
  };

  const handleSelect = async (selectedAddress: string): Promise<void> => {
    hasUserEditedAddressRef.current = true;
    selectionRequestIdRef.current += 1;
    const requestId = selectionRequestIdRef.current;

    try {
      const results = await geocodeByAddress(selectedAddress);
      const firstResult = results[0];

      if (selectionRequestIdRef.current !== requestId) {
        return;
      }

      if (!firstResult) {
        markAddressInvalid();
        return;
      }

      const latLng = await getLatLng(firstResult);

      if (selectionRequestIdRef.current !== requestId) {
        return;
      }

      const selection = parseAddressSelection({
        latLng,
        result: firstResult,
      });

      if (!selection) {
        markAddressInvalid();
        return;
      }

      applyLocationSelection(selection);
    } catch {
      if (selectionRequestIdRef.current === requestId) {
        markAddressInvalid();
      }
    }
  };

  return isLoaded ? (
    <>
      <PlacesAutocomplete
        value={addressString}
        onChange={handleAddressChange}
        onSelect={handleSelect}
        onError={(_, clearSuggestions) => {
          markAddressInvalid();
          clearSuggestions();
        }}
        highlightFirstSuggestion
      >
        {({ getInputProps, suggestions, loading, getSuggestionItemProps }) => (
          <DesignV2AutocompleteContainer>
            <DesignV2FieldWrapper
              $isFocused={isFocused}
              $hasError={!validAddress}
            >
              <DesignV2Input
                {...getInputProps({
                  placeholder: t("ProfileCompany.LocationPlaceholder"),
                  onFocus: () => setIsFocused(true),
                  onBlur: () => setIsFocused(false),
                })}
                $isFocused={isFocused}
              />
              {addressString && (
                <DesignV2ClearButton
                  type="button"
                  onClick={clearLocationData}
                  aria-label={t("General.Clear")}
                >
                  <CustomIcon
                    name="v2/cross"
                    size="24px"
                    color={ColorV2.Blue5}
                  />
                </DesignV2ClearButton>
              )}
            </DesignV2FieldWrapper>
            {(loading || suggestions.length > 0) && (
              <DesignV2SuggestionList>
                {loading && (
                  <DesignV2LoadingRow>
                    <CircularProgress size={18} />
                    {t("General.Loading")}
                  </DesignV2LoadingRow>
                )}
                {suggestions.map((suggestion, index) => (
                  <DesignV2SuggestionItem
                    {...getSuggestionItemProps(suggestion)}
                    key={
                      suggestion.placeId || `${suggestion.description}-${index}`
                    }
                    type="button"
                    $isActive={suggestion.active}
                  >
                    {suggestion.description}
                  </DesignV2SuggestionItem>
                ))}
              </DesignV2SuggestionList>
            )}
          </DesignV2AutocompleteContainer>
        )}
      </PlacesAutocomplete>
      {!noMap && (
        <GoogleMap
          mapContainerStyle={MAP_CONTAINER_STYLE}
          center={mapCenter}
          zoom={15}
        >
          {markerPosition && (
            <Marker title={companyName} position={markerPosition} />
          )}
        </GoogleMap>
      )}
    </>
  ) : (
    <>
      <CircularProgress />
    </>
  );
};
